import type {
  Annotation,
  Comment,
  Playlist,
  PlaylistItem,
  Project,
  SheetInfo,
  SheetLink,
  SheetProbe,
  SheetQueue,
  SheetStatusLabels,
  Shot,
  Version,
  VersionStatus,
  Viewer,
} from './types.ts';

/**
 * Thin wrapper over the same REST API any pipeline client uses. Cookies carry the guest
 * session; an admin token can be supplied for management screens.
 */
let adminToken = localStorage.getItem('openreview.token') ?? '';

export function setAdminToken(token: string): void {
  adminToken = token;
  if (token) localStorage.setItem('openreview.token', token);
  else localStorage.removeItem('openreview.token');
}

export function getAdminToken(): string {
  return adminToken;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

/**
 * Browsers cap the body of a `keepalive` request at 64 KiB and reject anything larger outright,
 * so a bigger payload goes out as an ordinary request instead — which may still make it.
 */
const KEEPALIVE_MAX_BYTES = 60_000;

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  opts?: { keepalive?: boolean },
): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (adminToken) headers.authorization = `Bearer ${adminToken}`;
  // A token holder acts under a chosen name rather than the bare "api": the server reads this
  // for attribution and stamps it onto the guest cookie the review room reuses. Guests carry
  // their name in that cookie already, so the header is only meaningful alongside the token.
  const chosenName = adminToken ? (localStorage.getItem('openreview.name')?.trim() ?? '') : '';
  if (chosenName) headers['x-openreview-user'] = chosenName;

  const json = body === undefined ? undefined : JSON.stringify(body);
  const res = await fetch(path, {
    method,
    headers,
    credentials: 'same-origin',
    body: json,
    // Lets the request outlive the page when it is sent while the tab is being closed.
    keepalive: opts?.keepalive === true && (json?.length ?? 0) < KEEPALIVE_MAX_BYTES,
  });

  const text = await res.text();
  if (!res.ok) {
    let message = text.slice(0, 300);
    try {
      const parsed = JSON.parse(text) as { error?: string; message?: string };
      message = parsed.error ?? parsed.message ?? message;
    } catch {
      /* keep raw text */
    }
    throw new ApiError(message, res.status);
  }
  return (text ? JSON.parse(text) : null) as T;
}

export const api = {
  health: () => request<{ ok: boolean; authRequired: boolean }>('GET', '/api/v1/health'),

  // library
  listProjects: () => request<{ projects: Project[] }>('GET', '/api/v1/projects'),
  createProject: (body: { name: string; code?: string; fps?: number }) =>
    request<Project>('POST', '/api/v1/projects', body),
  deleteProject: (id: string) => request<null>('DELETE', `/api/v1/projects/${id}`),
  setProjectThumbnail: (id: string, image: string) =>
    request<Project>('POST', `/api/v1/projects/${id}/thumbnail`, { image }),
  getProject: (id: string) =>
    request<{ project: Project; shots: Shot[]; versions: Version[] }>('GET', `/api/v1/projects/${id}`),
  createShot: (projectId: string, name: string) =>
    request<Shot>('POST', `/api/v1/projects/${projectId}/shots`, { name }),
  getVersion: (id: string) =>
    request<{
      version: Version;
      job: { state: string; progress: number; step: string | null } | null;
      proxyJob: { state: string; progress: number; step: string | null } | null;
    }>(
      'GET',
      `/api/v1/versions/${id}`,
    ),
  retranscode: (id: string) => request<{ jobId: string }>('POST', `/api/v1/versions/${id}/retranscode`, {}),
  deleteVersion: (id: string) => request<null>('DELETE', `/api/v1/versions/${id}`),

  // playlists
  listPlaylists: (projectId?: string) =>
    request<{ playlists: (Playlist & { shotCount: number })[] }>(
      'GET',
      projectId ? `/api/v1/playlists?projectId=${projectId}` : '/api/v1/playlists',
    ),
  createPlaylist: (body: { projectId: string; name: string; versionIds?: string[] }) =>
    request<{ playlist: Playlist; items: PlaylistItem[] }>('POST', '/api/v1/playlists', body),
  setPlaylistSettings: (id: string, body: { guestDownloads?: boolean; name?: string }) =>
    request<Playlist>('PATCH', `/api/v1/playlists/${id}`, body),
  deletePlaylist: (id: string) => request<null>('DELETE', `/api/v1/playlists/${id}`),
  getPlaylist: (id: string) =>
    request<{
      playlist: Playlist;
      items: PlaylistItem[];
      summary: { total: number; approved: number; rejected: number; pending: number };
    }>('GET', `/api/v1/playlists/${id}`),
  addPlaylistItems: (id: string, versionIds: string[]) =>
    request<{ items: PlaylistItem[] }>('POST', `/api/v1/playlists/${id}/items`, { versionIds }),
  removePlaylistItem: (id: string, itemId: string) =>
    request<{ items: PlaylistItem[] }>('DELETE', `/api/v1/playlists/${id}/items/${itemId}`),
  reorderPlaylist: (id: string, itemIds: string[]) =>
    request<{ items: PlaylistItem[] }>('POST', `/api/v1/playlists/${id}/reorder`, { itemIds }),

  // review
  setStatus: (
    versionId: string,
    body: {
      status: VersionStatus;
      comment?: string;
      frame?: number;
      author?: string;
      annotation?: { frame: number; strokes: unknown; png?: string };
    },
  ) =>
    request<{ version: Version; comment: Comment | null; annotation: Annotation | null }>(
      'PATCH',
      `/api/v1/versions/${versionId}/status`,
      body,
    ),
  listComments: (versionId: string) =>
    request<{ comments: Comment[]; annotations: Annotation[] }>('GET', `/api/v1/versions/${versionId}/comments`),
  addComment: (
    versionId: string,
    body: {
      body: string;
      frame?: number;
      author?: string;
      annotation?: { frame: number; strokes: unknown; png?: string };
    },
    opts?: { keepalive?: boolean },
  ) =>
    request<{ comment: Comment; annotation: Annotation | null }>(
      'POST',
      `/api/v1/versions/${versionId}/comments`,
      body,
      opts,
    ),
  deleteComment: (id: string) => request<null>('DELETE', `/api/v1/comments/${id}`),
  listAnnotations: (versionId: string) =>
    request<{ annotations: Annotation[] }>('GET', `/api/v1/versions/${versionId}/annotations`),

  // viewed-by (host only reads the list; every participant records their own view)
  recordView: (versionId: string, sessionId: string) =>
    request<void>('POST', `/api/v1/versions/${versionId}/view`, { sessionId }),
  getVersionViews: (versionId: string, sessionId: string) =>
    request<{ views: Viewer[] }>(
      'GET',
      `/api/v1/versions/${versionId}/views?sessionId=${encodeURIComponent(sessionId)}`,
    ),

  // google sheet mirroring
  getSheet: (projectId: string) => request<SheetInfo>('GET', `/api/v1/projects/${projectId}/sheet`),
  testSheet: (projectId: string, body: { url?: string; sheetTitle?: string; headerRow?: number }) =>
    request<SheetProbe>('POST', `/api/v1/projects/${projectId}/sheet/test`, body),
  saveSheet: (
    projectId: string,
    body: {
      url: string;
      sheetTitle: string;
      shotColumn: string;
      statusColumn: string;
      notesColumn: string | null;
      versionColumn: string | null;
      headerRow: number;
      statusLabels: SheetStatusLabels;
      stageColors?: Record<string, string>;
    },
  ) => request<{ link: SheetLink; queued: number; queue: SheetQueue }>('PUT', `/api/v1/projects/${projectId}/sheet`, body),
  unlinkSheet: (projectId: string) => request<null>('DELETE', `/api/v1/projects/${projectId}/sheet`),
  syncSheet: (projectId: string) =>
    request<{ queued: number; queue: SheetQueue }>('POST', `/api/v1/projects/${projectId}/sheet/sync`),
  /** Read the sheet back now; `withOptions` also re-reads its status dropdown. */
  pullSheet: (projectId: string, withOptions = false) =>
    request<
      | { linked: false }
      | { linked: true; statusOptions: string[]; statusLabels: SheetStatusLabels; lastError: string | null }
    >('POST', `/api/v1/projects/${projectId}/sheet/pull${withOptions ? '?options=1' : ''}`),
  setShotStage: (shotId: string, stage: string, comment?: string) =>
    request<{ ok: true }>('PUT', `/api/v1/shots/${shotId}/stage`, { stage, comment }),

  // sessions
  getSession: (id: string) =>
    request<{
      session: { id: string; name: string; playlistId: string };
      hasPassword: boolean;
      joined: boolean;
      canDownload: boolean;
      you: string | null;
      playlist: Playlist | null;
      items: PlaylistItem[] | null;
    }>('GET', `/api/v1/sessions/${id}`),
  joinSession: (id: string, body: { name: string; password?: string }) =>
    request<{
      session: { id: string; name: string; playlistId: string };
      you: string;
      playlist: Playlist;
      canDownload: boolean;
      items: PlaylistItem[];
    }>(
      'POST',
      `/api/v1/sessions/${id}/join`,
      body,
    ),
  createSession: (body: { playlistId: string; name?: string; password?: string }) =>
    request<{ session: { id: string; name: string }; joinUrl: string }>('POST', '/api/v1/sessions', body),
  // storage
  storage: () =>
    request<{
      dataDir: string;
      usage: {
        source: number;
        media: number;
        tmp: number;
        database: number;
        total: number;
        free: number | null;
      };
      counts: { projects: number; versions: number; ready: number };
      /** Bytes on disk per project id (transcodes + kept originals). */
      projects: Record<string, number>;
      orphans: {
        count: number;
        bytes: number;
        items: { path: string; bytes: number; kind: string; reason: string }[];
      };
    }>('GET', '/api/v1/storage'),
  cleanupStorage: () => request<{ removed: number; bytes: number }>('POST', '/api/v1/storage/cleanup', {}),

  listSessions: () =>
    request<{ sessions: { id: string; name: string; playlist_name: string; created_at: string }[] }>(
      'GET',
      '/api/v1/sessions',
    ),
};

/**
 * Resumable upload of one source file. Reports progress so a 4K ProRes upload does not look
 * like a hung browser tab.
 */
export async function uploadSource(
  versionId: string,
  file: File,
  onProgress?: (fraction: number) => void,
  /** Aborting stops the transfer mid-file; the promise then rejects with an AbortError. */
  signal?: AbortSignal,
): Promise<Version> {
  const aborted = (): DOMException => new DOMException('upload cancelled', 'AbortError');
  if (signal?.aborted) throw aborted();
  const { offset } = await request<{ offset: number }>('GET', `/api/v1/versions/${versionId}/upload/offset`);

  if (offset < file.size) {
    await new Promise<void>((resolve, reject) => {
      // XHR rather than fetch: it is still the only way to get upload progress events.
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', `/api/v1/versions/${versionId}/upload?offset=${offset}`);
      xhr.setRequestHeader('content-type', 'application/octet-stream');
      if (adminToken) xhr.setRequestHeader('authorization', `Bearer ${adminToken}`);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) onProgress((offset + e.loaded) / file.size);
      };
      xhr.onload = () =>
        xhr.status >= 200 && xhr.status < 300
          ? resolve()
          : reject(new ApiError(xhr.responseText.slice(0, 200), xhr.status));
      xhr.onerror = () => reject(new ApiError('network error during upload', 0));
      xhr.onabort = () => reject(aborted());
      signal?.addEventListener('abort', () => xhr.abort(), { once: true });
      xhr.send(file.slice(offset));
    });
  }
  if (signal?.aborted) throw aborted();

  onProgress?.(1);
  const done = await request<{ version: Version }>('POST', `/api/v1/versions/${versionId}/upload/complete`, {
    filename: file.name,
  });
  return done.version;
}

export async function createVersionForUpload(
  shotId: string,
  filename: string,
  name?: string,
): Promise<Version> {
  const res = await request<{ version: Version }>('POST', `/api/v1/shots/${shotId}/versions`, {
    filename,
    name,
  });
  return res.version;
}
